import {
  extractMultipleChoiceAnswers,
  compactMultipleChoiceAnswers,
  isCorrectMultipleSelect,
} from './examHelper';

/**
 * Processes a previously answered MULTIPLE_CHOICE question
 * @param {Object} lastExamItem - The exam item with the last submitted answer
 * @returns {Object} Object containing selected indices, correct indices and correctness status
 */
export const processMultipleChoiceSubmissionResult = (lastExamItem) => {
  const lastQuestionAnswer = lastExamItem.answer.value;
  const selectedChoiceIndex = lastExamItem.choices.findIndex(
    (choice) => choice.value === lastQuestionAnswer
  );

  const correctChoiceIndex = lastExamItem.choices.findIndex(
    (choice) => choice.value === lastExamItem.correctAnswer
  );

  return {
    selectedIndices: [selectedChoiceIndex],
    correctAnswerIndices: [correctChoiceIndex],
    isCorrect: lastExamItem.correctAnswer === lastQuestionAnswer,
  };
};

/**
 * Handles selecting an answer for a MULTIPLE_CHOICE question
 * @param {Object} currentExamItem - The current exam item
 * @param {number} answerIndex - The index of the selected answer
 * @returns {Object} Object containing the new selections and selected values
 */
export const selectMultipleChoiceAnswer = (currentExamItem, answerIndex) => {
  return {
    newSelections: [answerIndex],
    selectedValues: currentExamItem.choices[answerIndex].value,
  };
};

/**
 * Handles submitting an answer for a MULTIPLE_CHOICE question
 * @param {Object} question - The question being answered
 * @param {string} answer - The selected answer value
 * @param {Array<number>} selectedChoices - Array of selected choice indices
 * @returns {Object} Object containing correctness data and selected/correct indices
 */
export const submitMultipleChoiceAnswer = (question, answer, selectedChoices) => {
  const correctChoiceIndex = question.choices.findIndex(
    (choice) => choice.value === question.correctAnswer
  );

  return {
    isCorrect: question.correctAnswer === answer,
    selectedIndices: selectedChoices,
    correctAnswerIndices: [correctChoiceIndex],
  };
};

/**
 * Processes a previously answered MULTIPLE_SELECT question
 * @param {Object} lastExamItem - The exam item with the last submitted answer
 * @returns {Object} Object containing selected indices, correct indices and correctness status
 */
export const processMultipleSelectSubmissionResult = (lastExamItem) => {
  const answerValues = extractMultipleChoiceAnswers(lastExamItem.answer.value);
  const selectedIndices = answerValues
    .map((value) => lastExamItem.choices.findIndex((choice) => choice.value === value))
    .filter((idx) => idx !== -1);

  const correctAnswers = extractMultipleChoiceAnswers(lastExamItem.correctAnswer);
  const correctAnswerIndices = correctAnswers
    .map((value) => lastExamItem.choices.findIndex((choice) => choice.value === value))
    .filter((idx) => idx !== -1);

  return {
    selectedIndices,
    correctAnswerIndices,
    isCorrect: isCorrectMultipleSelect(
      answerValues,
      extractMultipleChoiceAnswers(lastExamItem.correctAnswer)
    ),
  };
};

/**
 * Toggles an answer for a MULTIPLE_SELECT question
 * @param {Object} currentExamItem - The current exam item
 * @param {number} answerIndex - The index of the answer to toggle
 * @param {Array<number>} selectedChoices - Currently selected choices
 * @returns {Object} Object containing the new selections and selected values
 */
export const toggleMultipleSelectAnswer = (currentExamItem, answerIndex, selectedChoices) => {
  let newSelections = [...selectedChoices];

  if (newSelections.includes(answerIndex)) {
    newSelections = newSelections.filter((idx) => idx !== answerIndex);
  } else {
    newSelections.push(answerIndex);
  }

  const selectedValues = compactMultipleChoiceAnswers(newSelections, currentExamItem.choices);

  return {
    newSelections,
    selectedValues,
  };
};

/**
 * Handles submitting an answer for a MULTIPLE_SELECT question
 * @param {Object} question - The question being answered
 * @param {string} answer - The selected answer value (compact form)
 * @param {Array<number>} selectedChoices - Array of selected choice indices
 * @returns {Object} Object containing correctness data and selected/correct indices
 */
export const submitMultipleSelectAnswer = (question, answer, selectedChoices) => {
  const selectedValues = extractMultipleChoiceAnswers(answer || '');
  const correctAnswers = extractMultipleChoiceAnswers(question.correctAnswer);

  const correctAnswerIndices = correctAnswers
    .map((value) => question.choices.findIndex((choice) => choice.value === value))
    .filter((idx) => idx !== -1);

  return {
    selectedIndices: selectedChoices,
    isCorrect: isCorrectMultipleSelect(selectedValues, correctAnswers),
    correctAnswerIndices,
  };
};
